import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Icon, Label } from 'office-ui-fabric-react';
import ReactTable, { CellInfo, Column } from 'react-table';

import { ApiUrls, PersonFleetOverrideType, Permissions } from '@app/AppConstants';
import { PersonFleetModel, PersonFleetOverrideModel, PersonFleetOverrideValidationResultModel } from '@app/Models';

import InlineDateValidationService from '@app/Services/InlineDateValidationService';
import ApiService from '@app/Services/ApiService';
import { securityService } from '@app/Services/SecurityService';
import DateTimeService from '@app/Services/DateTimeService';

import { CompletionType, PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { Loading } from '@app/Components/Loading';
import { InlineDateCell } from '@app/Components/CellFormatters/InlineDateCell';
import { InlinePersonFleetOverrideTypeDropdownCell } from '@app/Components/CellFormatters/InlinePersonFleetOverrideTypeDropdownCell';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { NewPersonFleetOverrideDialog } from './Dialogs/NewPersonFleetOverrideDialog';
import { modalService, ModalButtonType } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

export type PersonFleetTablesBlockProps = {
    personId: string;
};

@observer
export class PersonFleetTablesBlock extends React.Component<PersonFleetTablesBlockProps,{}>{
    private _store: PersonFleetTablesBlockStore;

    constructor(props: PersonFleetTablesBlockProps){
        super(props);
        this._store = new PersonFleetTablesBlockStore();
    }
    
    componentDidUpdate(prevProps: Readonly<PersonFleetTablesBlockProps>){
        if(prevProps.personId !== this.props.personId){
            this._store.personId = this.props.personId;
            this._store.loadPersonFleets(this._store.personId);
            this._store.loadPersonFleetOverrides(this._store.personId);
        }
    }
    render(){
        const {personId} = this.props;
        const {personFleets, personFleetOverrides, personFleetDataLoaded, personFleetOverridesDataLoaded} = this._store;
        if (!personId) return;

        const { hasPermission, permissionCss } = securityService;
        return(
            <>
                <div className="row">
                    <div className="col-6">
                        <Loading loading={personFleetDataLoaded.isPending} />
                        <div className="table-label">
                            <Label>Fleets</Label>
                        </div>
                        {personFleets && hasPermission(Permissions.ViewObjectPersonFleet, true) &&
                            <ReactTable
                                data={personFleets}
                                columns={this._personFleetColumns}
                                pageSize={personFleets.length}
                                showPageSizeOptions={false}
                                showPagination={false}
                                className={'ReactTable-fixed-height' + permissionCss(Permissions.ViewObjectPersonFleet, Permissions.EditObjectPersonFleet)}
                            />
                        }
                    </div>
                    <div className="col-6">
                        <Loading loading={personFleetOverridesDataLoaded.isPending} />
                        <div className="table-label">
                            <Label>Fleets Overrides</Label>
                            {hasPermission(Permissions.ViewAddObjectPersonFleetOverride, !AzureAuth.isReadOnly) && 
                                <button className={'table-label-button' + permissionCss(Permissions.ViewObjectPersonFleetOverride, Permissions.EditObjectPersonFleetOverride)} onClick={this._addPersonFleetOverride}>
                                    <Icon iconName="Add" className="ms-IconTable" />
                                </button>
                            }
                        </div>
                        {personFleetOverrides && hasPermission(Permissions.ViewObjectPersonFleetOverride, true) &&
                            <ReactTable<PersonFleetOverrideModel>
                                data={personFleetOverrides}
                                columns={this._personFleetOverridesColumns}
                                pageSize={personFleetOverrides.length}
                                showPageSizeOptions={false}
                                showPagination={false}
                                className={'ReactTable-fixed-height' + permissionCss(Permissions.ViewObjectPersonFleetOverride, Permissions.EditObjectPersonFleetOverride)}
                            />
                        }
                    </div>
                </div>
            </>
        );
    }

    @action.bound
    private async _addPersonFleetOverride() {
        const { button } = await modalService.show(NewPersonFleetOverrideDialog, {
            personId: this.props.personId
        });
        
        if (button === ModalButtonType.Save){
            await this._store.loadPersonFleetOverrides(this.props.personId);
        }
    }
    
    private _renderPersonFleetDateFrom(cellInfo: CellInfo){
        const personFleet  = cellInfo.row as PersonFleetModel;
        if(!personFleet)
            return null;
        return <span>{personFleet.dateFrom ? DateTimeService.toUiDate(personFleet.dateFrom) : '\u221E'} </span>;
    }
    
    private _renderPersonFleetDateTo(cellInfo: CellInfo){
        const personFleet  = cellInfo.row as PersonFleetModel;
        if(!personFleet)
            return null;
        return <span>{personFleet.dateTo ? DateTimeService.toUiDate(personFleet.dateTo) : '\u221E'}</span>;
    }
    private _renderPersonFleetType(cellInfo: CellInfo){
        const personFleet  = cellInfo.row as PersonFleetModel;
        if(!personFleet)
            return null;
        return <span>{personFleet.type}</span>;
    }

    private _renderPersonFleetOverrideDateFrom = (cellInfo: CellInfo) => {
        return <InlineDateCell<PersonFleetOverrideModel>
	    onValidate={InlineDateValidationService.validateFromDate} 
	    value={cellInfo.value} onSelect={this._store.onFleetDateFromChange} 
	    row={cellInfo.row._original} 
	    fieldKey={cellInfo.column.id as keyof PersonFleetOverrideModel} 
	    isReadOnly={!securityService.hasPermission(Permissions.EditPersonFleetOverrideDateFrom, !AzureAuth.isReadOnly)}/>;
    };

    private _renderPersonFleetOverrideDateTo = (cellInfo: CellInfo) => {
        return <InlineDateCell<PersonFleetOverrideModel>
	    onValidate={InlineDateValidationService.validateDate} 
	    value={cellInfo.value} 
	    onSelect={this._store.onFleetDateToChange} 
	    row={cellInfo.row._original} 
	    fieldKey={cellInfo.column.id as keyof PersonFleetOverrideModel}
	    isReadOnly={!securityService.hasPermission(Permissions.EditPersonFleetOverrideDateTo, !AzureAuth.isReadOnly)}/>;
    };

    private _renderPersonFleetOverrideType = (cellInfo: CellInfo) => {
        return <InlinePersonFleetOverrideTypeDropdownCell 
            value={cellInfo.value} 
            onChangeHandler={this._store.onFleetTypeChange}  
            overruleId={cellInfo.column.id || ''} 
            disabled={!securityService.hasPermission(Permissions.EditPersonFleetOverrideType, !AzureAuth.isReadOnly)}
        />;
    };

    @action.bound
    private _renderDeletePersonFleetOverrideColumn(cellInfo: CellInfo){
        return <i className="ms-Icon ms-Icon-action ms-Icon--Delete" aria-hidden="true" onClick={async () => this._onDeletePersonPersonFleetOverride(cellInfo)} />;
    }

    private async _onDeletePersonPersonFleetOverride(cellInfo: CellInfo) {
        await modalService.showConfirmation('Are you sure you want to delete this person fleet override?', 'Warning', {
            onConfirm: async () => {
                await this._store.deletePersonFleetOverride(cellInfo.value);
            }
        });
    }
    
    private _personFleetColumns: Column[] = [
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderPersonFleetDateFrom
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderPersonFleetDateTo
        },
        {
            Header: 'Fleet Type',
            accessor: 'type',
            Cell: this._renderPersonFleetType
        }
    ];

    private _personFleetOverridesColumns: Column<PersonFleetOverrideModel>[] = [
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderPersonFleetOverrideDateFrom,
            show: securityService.hasPermission(Permissions.ViewPersonFleetOverrideDateFrom),
            className: securityService.permissionCss(Permissions.ViewPersonFleetOverrideDateFrom, Permissions.EditPersonFleetOverrideDateFrom)
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderPersonFleetOverrideDateTo,
            show: securityService.hasPermission(Permissions.ViewPersonFleetOverrideDateTo),
            className: securityService.permissionCss(Permissions.ViewPersonFleetOverrideDateTo, Permissions.EditPersonFleetOverrideDateTo)
        },
        {
            Header: 'Fleet Type',
            accessor: 'type',
            Cell: this._renderPersonFleetOverrideType,
            show: securityService.hasPermission(Permissions.ViewPersonFleetOverrideType),
            className: securityService.permissionCss(Permissions.ViewPersonFleetOverrideType, Permissions.EditPersonFleetOverrideType)
        },
        {
            Header: '',
            accessor: 'id',
            sortable: false,
            Cell: this._renderDeletePersonFleetOverrideColumn,
            show: securityService.hasPermission(Permissions.EditObjectPersonFleetOverride, !AzureAuth.isReadOnly),
            maxWidth: 50,
            className: securityService.permissionCss(Permissions.ViewObjectPersonFleetOverride, Permissions.EditObjectPersonFleetOverride)
        }
    ];
}

class PersonFleetTablesBlockStore{
    @observable personFleets: PersonFleetModel[] = [];
    @observable personFleetDataLoaded: PromiseCompletion = new PromiseCompletion(CompletionType.Completed);
    @observable personFleetOverrides: PersonFleetOverrideModel[] = [];
    @observable personFleetOverridesDataLoaded: PromiseCompletion = new PromiseCompletion(CompletionType.Completed);
    @observable personId: string;

    @action.bound
    async loadPersonFleets(personId: string) {
        const params = { personId: personId };
        const fleetRequest = ApiService.getTypedData<PersonFleetModel[]>(ApiUrls.PersonFleetUrl, params);
        this.personFleetDataLoaded.subscribe(fleetRequest);
        const {data} = await fleetRequest;
        this.personFleets = data;
    }

    @action.bound
    async loadPersonFleetOverrides(personId: string) {
        const params = { personId: personId };
        const fleetOverridesRequest = ApiService.getTypedData<PersonFleetOverrideModel[]>(ApiUrls.PersonFleetOverrideUrl, params);
        this.personFleetOverridesDataLoaded.subscribe(fleetOverridesRequest);
        const {data} = await fleetOverridesRequest;
        this.personFleetOverrides = data;
    }
    
    @action.bound
    onFleetTypeChange(overrideId: string, newType: PersonFleetOverrideType){
        const fleetOverride = this.personFleetOverrides.find(x => x.id === overrideId);
        if(fleetOverride){
            fleetOverride.type = newType as keyof PersonFleetOverrideType;
            this.updatePersonFleetOverride(fleetOverride);
        }
    }

    @action.bound
    onFleetDateFromChange(model: PersonFleetOverrideModel){
        const fleetOverride = this.personFleetOverrides.find(x => x.id === model.id);
        if(fleetOverride){
            fleetOverride.dateFrom = model.dateFrom;
            this.updatePersonFleetOverride(fleetOverride);
        }
    }

    @action.bound
    onFleetDateToChange(model: PersonFleetOverrideModel){
        const fleetOverride = this.personFleetOverrides.find(x => x.id === model.id);
        if (fleetOverride){
            fleetOverride.dateTo = model.dateTo;
            this.updatePersonFleetOverride(fleetOverride);
        }
    }
    
    @action.bound
    async updatePersonFleetOverride(overrideModel: PersonFleetOverrideModel){
        const { data: validatedModel } = await  ApiService.putTypedData<PersonFleetOverrideValidationResultModel>(ApiUrls.PersonFleetOverrideUrl, overrideModel, { completion: this.personFleetDataLoaded});

        if (validatedModel.hasErrors && validatedModel.errors) {
            await modalService.showConfirmation(validatedModel.errors.join('\r\n '));
        } else {
            NotificationHandler.showSuccess('Person Fleet Overrides was updated', 'Success message');
        }
        const updatedPersonFleetOverride = validatedModel.value;
        if (updatedPersonFleetOverride) {
            this.personFleetOverrides = this.personFleetOverrides.map((item: PersonFleetOverrideModel) => item.id === updatedPersonFleetOverride.id ? updatedPersonFleetOverride : item);
        }
    }

    @action.bound
    async deletePersonFleetOverride(id: string) {
        await ApiService.deleteData(ApiUrls.PersonFleetOverrideUrl + '?fleetOverrideId=' + id);
        NotificationHandler.showSuccess('Person Planing Autonomy was deleted', 'Success message');
        if (this.personId) this.loadPersonFleetOverrides(this.personId);
    }
    
}